/**
 * ScoreCard Component
 * Phase 7: Displays an audit score with visual representation and explanation.
 * 
 * Scores are displayed on a 0-100 scale with appropriate semantic colors:
 * - Risk/Debt scores: Higher is worse (red at 100)
 * - Compliance scores: Higher is better (green at 100)
 */

import '../lib/reviewerTheme'
import { useLocale } from '../contexts/LocaleContext'

export interface ScoreCardProps {
  /** Score label */
  label: string
  
  /** Score value (0-100) or null if not available */
  score: number | null
  
  /** Score type for semantic coloring */
  type: 'risk' | 'debt' | 'compliance'
  
  /** Optional explanation text */
  explanation?: string
  
  /** Whether to show "Not Evaluated" for null scores */
  showNotEvaluated?: boolean
  
  /** Optional onClick handler */
  onClick?: () => void
}

/**
 * Get score color based on type and value.
 * Risk/Debt: Red is bad (high scores are worse)
 * Compliance: Green is good (high scores are better)
 */
function getScoreColor(score: number, type: 'risk' | 'debt' | 'compliance'): string {
  if (type === 'compliance') {
    // Compliance: higher is better
    if (score >= 80) return 'var(--severity-low)' // Green
    if (score >= 60) return 'var(--severity-info)' // Blue
    if (score >= 40) return 'var(--severity-medium)' // Yellow
    return 'var(--severity-high)' // Orange/Red
  } else {
    // Risk/Debt: higher is worse
    if (score >= 70) return 'var(--severity-critical)' // Red
    if (score >= 50) return 'var(--severity-high)' // Orange
    if (score >= 30) return 'var(--severity-medium)' // Yellow
    return 'var(--severity-low)' // Green
  }
}

function getScoreGradeKey(score: number, type: 'risk' | 'debt' | 'compliance'): string {
  if (type === 'compliance') {
    if (score >= 90) return 'scores.grade.excellent'
    if (score >= 75) return 'scores.grade.good'
    if (score >= 60) return 'scores.grade.acceptable'
    if (score >= 40) return 'scores.grade.poor'
    return 'scores.grade.critical'
  }
  if (score >= 80) return 'scores.grade.critical'
  if (score >= 60) return 'scores.grade.high'
  if (score >= 40) return 'scores.grade.medium'
  if (score >= 20) return 'scores.grade.low'
  return 'scores.grade.minimal'
}

export function ScoreCard({
  label,
  score,
  type,
  explanation,
  showNotEvaluated = true,
  onClick,
}: ScoreCardProps) {
  const { t } = useLocale()
  const isClickable = !!onClick
  const grade = score !== null ? t(getScoreGradeKey(score, type)) : t('scores.notEvaluated')
  
  return (
    <div
      data-testid="score-card"
      className={`card ${isClickable ? 'cursor-pointer hover:border-[var(--accent)] transition-colors' : ''}`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      aria-label={
        score !== null
          ? t('scores.ariaValue', { label, score: score.toFixed(1), grade })
          : t('scores.ariaNotEvaluated', { label })
      }
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-xs font-medium text-[var(--text-tertiary)] mb-2">
            {label}
          </div>
          
          {score !== null ? (
            <>
              <div className="flex items-baseline gap-2 mb-2">
                <span
                  data-testid="score-value"
                  className="text-3xl font-medium"
                  style={{ color: getScoreColor(score, type) }}
                  aria-label={t('scores.ariaScoreValue', { score: score.toFixed(1) })}
                >
                  {score.toFixed(1)}
                </span>
                <span className="text-sm text-[var(--text-secondary)]">/ 100</span>
              </div>
              
              <div
                className="text-xs font-medium mb-3"
                style={{ color: getScoreColor(score, type) }}
              >
                {grade}
              </div>
              
              {/* Score bar visualization */}
              <div className="w-full h-2 bg-[var(--surface-secondary)] rounded-sm overflow-hidden">
                <div
                  className="h-full transition-all duration-300"
                  style={{
                    width: `${score}%`,
                    backgroundColor: getScoreColor(score, type),
                  }}
                  role="progressbar"
                  aria-valuenow={score}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              
              {explanation && (
                <div className="text-xs text-[var(--text-secondary)] mt-3 leading-relaxed">
                  {explanation}
                </div>
              )}
            </>
          ) : (
            showNotEvaluated && (
              <div className="text-sm text-[var(--text-tertiary)]">
                {t('scores.notEvaluated')}
              </div>
            )
          )}
        </div>
        
        {isClickable && (
          <svg
            className="w-4 h-4 text-[var(--text-tertiary)] flex-shrink-0 mt-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        )}
      </div>
    </div>
  )
}
